import { useContext, useEffect } from "react";
import { ApiContext, useApi } from "../apiProvider";
import { ApiVersionName, ResultObj, allFetch } from "../Domain";
import { ApiManagerBase, DefaultAdapterBase } from "../AdapterRule";
import { resolveModuleName } from "typescript";

/**
 * 產生apiManager實例，並透過factory概念，將產生好的東西放到Provider，往後要用就是從Provider拿出來用
 * @returns 
 */
export const useChatbotApi = () => {
    const apiManagerName = "chatbotApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useCarApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newChatbotApiManager = new ChatbotApiManager();
            newChatbotApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing Chatbot API Manager", e);
            });
        }
    }, [apiManagers]);

    return useApi<ChatbotApiManager>(apiManagerName);
};

/** ChatbotApiManager 本人 */
export class ChatbotApiManager extends ApiManagerBase<ChatbotDefaultAdapter> implements ChatbotApiInterface {
    // 註冊相對應 adapter version
    apiManagerVersion: Record<ApiVersionName | "defaultAdapter", ChatbotDefaultAdapter> = {
        [ApiVersionName.DOT_NET_6]: new ChatbotDotnet6Adapter(),
        [ApiVersionName.DOT_NET_6_AM_N]: new ChatbotDotnet6AMNAdapter(),
        [ApiVersionName.FRAMEWORK]: new ChatbotFrameworkAdapter(),
        defaultAdapter: new ChatbotDefaultAdapter(),
    }
    // 定義初始值
    adapter = this.apiManagerVersion.defaultAdapter;
    Chatbot = this.checkDefaultHOF(async (obj: ChatbotParams) => await this.adapter.Chatbot(obj));
    ChatRecordSearch = this.checkDefaultHOF(async (obj: ChatRecordSearchParams) => await this.adapter.ChatRecordSearch(obj));
}

/** 定義 API interface */
export interface ChatbotApiInterface {
    /** 小助手機器人 */
    Chatbot: (obj: ChatbotParams) => Promise<ResultObj>;
    /** 查詢使用者對話紀錄 */
    ChatRecordSearch: (obj: ChatRecordSearchParams) => Promise<ResultObj>;
}

/** 預設 api adapter */
class ChatbotDefaultAdapter extends DefaultAdapterBase implements ChatbotApiInterface {
    // #region 預設 API 方法
    Chatbot = async (obj: ChatbotParams): Promise<ResultObj> => await allFetch('Chatbot/Chatbot', obj);
    ChatRecordSearch = async (obj: ChatRecordSearchParams): Promise<ResultObj> => await allFetch('Chatbot/ChatRecordSearch', obj);
}

/** DotNet6 adapter */
class ChatbotDotnet6Adapter extends ChatbotDefaultAdapter {
}

/** DotNet6 AMN adapter */
class ChatbotDotnet6AMNAdapter extends ChatbotDotnet6Adapter {
}

/** Framework adapter */
class ChatbotFrameworkAdapter extends ChatbotDefaultAdapter {
}

/**
 * 小助手機器人
 * @param msg 問答文字訊息
 * @param msg_type 問答類型:文字、圖片、檔案等
 * @param tag 聊天室標籤
 */
export interface ChatbotParams {
    msg: string;
    msg_type: string;
    tag: string;
}
/**
 * 查詢使用者對話紀錄
 * @param user_id 使用者 id
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export interface ChatRecordSearchParams {
    user_id: string,
    page: 0,
    num_per_page: 0
}